"use client"

import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Save, Upload, AlertTriangle } from "lucide-react"
import { toast } from "sonner"
import type { MappingConfig } from "@/types/fbo-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface JsonTabProps {
  config: MappingConfig
  onConfigChange: (config: MappingConfig) => void
  hasInvalidMappings: boolean
  onSave?: () => void
}

export function JsonTab({ config, onConfigChange, hasInvalidMappings, onSave }: JsonTabProps) {
  const { t } = useTranslation()
  const [jsonInput, setJsonInput] = useState("")

  // Sync jsonInput with config changes
  useEffect(() => {
    setJsonInput(JSON.stringify(config, null, 2))
  }, [config])

  const handleLoadJson = () => {
    try {
      const parsedConfig = JSON.parse(jsonInput) as MappingConfig
      onConfigChange(parsedConfig)

      toast.success(t("jsonTab.toasts.loadedTitle"), {
        description: t("jsonTab.toasts.loaded"),
      })
    } catch (error) {
      toast.error(t("jsonTab.toasts.errorTitle"), {
        description: t("jsonTab.toasts.invalidJson"),
      })
    }
  }

  const handleSaveJson = () => {
    let parsedConfig: MappingConfig

    try {
      // Parse the edited JSON input
      parsedConfig = JSON.parse(jsonInput) as MappingConfig
    } catch (error) {
      toast.error(t("jsonTab.toasts.errorTitle"), {
        description: t("jsonTab.toasts.invalidJson"),
      })
      return
    }

    // Update the config with the parsed JSON
    onConfigChange(parsedConfig)
    
    // Call the save function to persist to API
    if (onSave) {
      onSave()
    }
  }

  return (
    <div>
      <div className="space-y-4">
        {/* Warning for invalid mappings */}
        {hasInvalidMappings && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("jsonTab.alerts.invalidTitle")}</AlertTitle>
            <AlertDescription>
              {t("jsonTab.alerts.invalidDescription")}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="jsonConfig">{t("jsonTab.labels.configuration")}</Label>
          <textarea
            id="jsonConfig"
            className="h-[60vh] w-full resize-none rounded-md border border-control-border bg-card p-4 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />
        </div>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleLoadJson}>
            <Upload className="h-4 w-4 mr-2" />
            {t("jsonTab.actions.loadJson")}
          </Button>
          <Button onClick={handleSaveJson} disabled={hasInvalidMappings}>
            <Save className="h-4 w-4 mr-2" />
            {t("jsonTab.actions.saveJson")}
          </Button>
        </div>
      </div>
    </div>
  )
} 